import {Link as RouterLink} from 'react-router-dom'
import {Trans, useTranslation} from 'react-i18next'
import ContactUsButton from '../components/ContactUsButton'
import './RefundPage.css'

interface TocEntry {
  readonly id: string
  readonly key: string
}

const TOC: readonly TocEntry[] = [
  {id: 'seller', key: 'seller'},
  {id: 'subject', key: 'subject'},
  {id: 'consent', key: 'consent'},
  {id: 'when-yes', key: 'whenYes'},
  {id: 'when-no', key: 'whenNo'},
  {id: 'claim', key: 'claim'},
  {id: 'refund-flow', key: 'refundFlow'},
  {id: 'non-eu', key: 'nonEu'},
  {id: 'ars-odr', key: 'arsOdr'},
  {id: 'law', key: 'law'},
  {id: 'changes', key: 'changes'},
]

const PROSE_COMPONENTS = {
  refund: <RouterLink to="/refund" />,
  terms: <RouterLink to="/terms" />,
  privacy: <RouterLink to="/privacy" />,
  contact: <RouterLink to="/contact" />,
  account: <RouterLink to="/account" />,
  soi: <a href="https://www.soi.sk" target="_blank" rel="noreferrer" />,
  odr: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noreferrer" />,
  strong: <strong />,
  em: <em />,
}

const pad2 = (n: number) => String(n).padStart(2, '0')

const RefundPage = () => {
  const {t} = useTranslation()

  return (
    <div className="refund-root">
      <div className="rf-grid" aria-hidden />

      <main className="refund-page">
        <header className="rf-hero">
          <span className="rf-eyebrow">
            <span className="rf-eyebrow-dot" />
            {t('refund.eyebrow')}
          </span>
          <h1 className="rf-title">
            {t('refund.titleLead')} <em>{t('refund.titleEm')}</em>
          </h1>
          <p className="rf-lede">{t('refund.lede')}</p>
          <div className="rf-meta">
            <span>{t('refund.metaUpdated')}</span>
            <span className="rf-meta-sep" />
            <span>{t('refund.metaReading')}</span>
            <span className="rf-meta-sep" />
            <span>{t('refund.metaGoverned')}</span>
          </div>
        </header>

        <div className="rf-layout">
          <aside className="rf-toc" aria-label={t('refund.tocLabel')}>
            <div className="rf-toc-label">{t('refund.tocLabel')}</div>
            {TOC.map((entry, i) => (
              <a key={entry.id} href={`#${entry.id}`}>
                <span className="rf-toc-num">{pad2(i + 1)}</span>
                <span>{t(`refund.toc.${entry.key}`)}</span>
              </a>
            ))}
          </aside>

          <article className="rf-prose">
            <section id="seller">
              <h2 data-num="01">{t('refund.sections.seller.title')}</h2>
              <p>{t('refund.sections.seller.p1')}</p>
              <h3>{t('refund.sections.seller.operatorHeading')}</h3>
              <div className="rf-address">
                <Trans i18nKey="refund.sections.seller.operatorBody" components={{br: <br />, strong: <strong />}} />
              </div>
              <p>
                <Trans i18nKey="refund.sections.seller.operatorRole" components={PROSE_COMPONENTS} />
              </p>
              <h3>{t('refund.sections.seller.morHeading')}</h3>
              <p>
                <Trans i18nKey="refund.sections.seller.morBody" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="refund.sections.seller.closing" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="subject">
              <h2 data-num="02">{t('refund.sections.subject.title')}</h2>
              <p>
                <Trans i18nKey="refund.sections.subject.p1" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="refund.sections.subject.p2" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="refund.sections.subject.p3" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="consent">
              <h2 data-num="03">{t('refund.sections.consent.title')}</h2>
              <p>
                <Trans i18nKey="refund.sections.consent.p1" components={PROSE_COMPONENTS} />
              </p>
              <p>{t('refund.sections.consent.p2')}</p>
              <ol>
                <li>
                  <Trans i18nKey="refund.sections.consent.steps.modal" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="refund.sections.consent.steps.statement" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="refund.sections.consent.steps.gate" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="refund.sections.consent.steps.record" components={PROSE_COMPONENTS} />
                </li>
              </ol>
              <p>
                <Trans i18nKey="refund.sections.consent.fallback" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="when-yes">
              <h2 data-num="04">{t('refund.sections.whenYes.title')}</h2>
              <p>{t('refund.sections.whenYes.p1')}</p>
              <ul>
                <li>
                  <Trans i18nKey="refund.sections.whenYes.bullets.untouched" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="refund.sections.whenYes.bullets.tech" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="refund.sections.whenYes.bullets.duplicate" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="refund.sections.whenYes.bullets.unauthorised" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="refund.sections.whenYes.bullets.defect" components={PROSE_COMPONENTS} />
                </li>
              </ul>
            </section>

            <section id="when-no">
              <h2 data-num="05">{t('refund.sections.whenNo.title')}</h2>
              <p>{t('refund.sections.whenNo.p1')}</p>
              <ul>
                <li>
                  <Trans i18nKey="refund.sections.whenNo.bullets.used" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="refund.sections.whenNo.bullets.changeOfMind" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="refund.sections.whenNo.bullets.expired" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="refund.sections.whenNo.bullets.dislike" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="refund.sections.whenNo.bullets.breach" components={PROSE_COMPONENTS} />
                </li>
              </ul>
            </section>

            <section id="claim">
              <h2 data-num="06">{t('refund.sections.claim.title')}</h2>
              <p>
                <Trans i18nKey="refund.sections.claim.p1" components={PROSE_COMPONENTS} />
              </p>
              <ul>
                <li>
                  <Trans i18nKey="refund.sections.claim.bullets.orderId" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="refund.sections.claim.bullets.email" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="refund.sections.claim.bullets.issue" components={PROSE_COMPONENTS} />
                </li>
                <li>
                  <Trans i18nKey="refund.sections.claim.bullets.evidence" components={PROSE_COMPONENTS} />
                </li>
              </ul>
              <p>
                <Trans i18nKey="refund.sections.claim.ack" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="refund.sections.claim.timing" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="refund-flow">
              <h2 data-num="07">{t('refund.sections.refundFlow.title')}</h2>
              <p>
                <Trans i18nKey="refund.sections.refundFlow.p1" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="refund.sections.refundFlow.p2" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="refund.sections.refundFlow.p3" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="refund.sections.refundFlow.p4" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="non-eu">
              <h2 data-num="08">{t('refund.sections.nonEu.title')}</h2>
              <p>
                <Trans i18nKey="refund.sections.nonEu.p1" components={PROSE_COMPONENTS} />
              </p>
              <p>{t('refund.sections.nonEu.p2')}</p>
            </section>

            <section id="ars-odr">
              <h2 data-num="09">{t('refund.sections.arsOdr.title')}</h2>
              <p>{t('refund.sections.arsOdr.p1')}</p>
              <div className="rf-address">
                <Trans
                  i18nKey="refund.sections.arsOdr.soiBody"
                  components={{
                    br: <br />,
                    strong: <strong />,
                    soi: <a href="https://www.soi.sk" target="_blank" rel="noreferrer" />,
                  }}
                />
              </div>
              <p>
                <Trans i18nKey="refund.sections.arsOdr.odrBody" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="refund.sections.arsOdr.closing" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="law">
              <h2 data-num="10">{t('refund.sections.law.title')}</h2>
              <p>
                <Trans i18nKey="refund.sections.law.p1" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="refund.sections.law.p2" components={PROSE_COMPONENTS} />
              </p>
              <p>
                <Trans i18nKey="refund.sections.law.p3" components={PROSE_COMPONENTS} />
              </p>
            </section>

            <section id="changes">
              <h2 data-num="11">{t('refund.sections.changes.title')}</h2>
              <p>{t('refund.sections.changes.p1')}</p>
              <p>{t('refund.sections.changes.p2')}</p>
              <p>
                <Trans i18nKey="refund.sections.changes.p3" components={PROSE_COMPONENTS} />
              </p>
              <div className="rf-contact-cta">
                <ContactUsButton />
              </div>
            </section>
          </article>
        </div>
      </main>
    </div>
  )
}

export default RefundPage
